import * as React from "react";
import { useLocation, useRoute } from "wouter";
import { ArrowLeft, MapPin, CheckCircle2, AlertTriangle, Pause, MoreHorizontal } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";
import { useSpaces } from "@/hooks/useSpaces.js";
import { useAuth } from "@/contexts/AuthContext.js";
import type { GoalDetail, GoalType } from "./types.js";
import { GOAL_TYPE_LABELS } from "./types.js";
import { PlacementGoalView } from "./views/PlacementGoalView.js";
import { ReorganizeGoalView } from "./views/ReorganizeGoalView.js";
import { DeclutterGoalView } from "./views/DeclutterGoalView.js";
import { RoutineGoalView } from "./views/RoutineGoalView.js";

/**
 * GoalDetailPage — thin dispatcher.
 *
 * Owns only: routing, goal fetch, loading/error, the shared header/chrome
 * (type badge, status, lifecycle overflow menu + edit/abandon modals), and a
 * `switch(goal.type)` that renders a per-type flow view. All the per-type body
 * logic (placement machinery, declutter suggestions, routine trials) lives in
 * the bounded view components under ./views, over the shared PlacementEngine.
 *
 * This replaced a ~1,969-line monolith that hid sections via booleans; the
 * split is a pure refactor — behavior for every goal type is unchanged.
 */
export default function GoalDetailPage() {
  const [, navigate] = useLocation();
  const [, params] = useRoute("/organize/goals/:id");
  const toast = useToast();
  const { spaces } = useSpaces();
  const { user } = useAuth();

  const [goal, setGoal] = React.useState<GoalDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // Lifecycle controls
  const [showOverflowMenu, setShowOverflowMenu] = React.useState(false);
  const [showEditGoal, setShowEditGoal] = React.useState(false);
  const [showAbandonConfirm, setShowAbandonConfirm] = React.useState(false);
  const [editTitle, setEditTitle] = React.useState("");
  const [editDescription, setEditDescription] = React.useState("");
  const [editLocationId, setEditLocationId] = React.useState("");
  const [achievePending, setAchievePending] = React.useState(false);

  const goalId = params?.id;
  const isOwner = goal ? goal.userId === user?.id : false;

  const fetchGoal = React.useCallback(async () => {
    if (!goalId) return;
    setLoadError(null);
    try {
      const data = await apiFetch<GoalDetail>(`/api/organizing-goals/${goalId}`);
      setGoal(data);
    } catch (err: any) {
      setLoadError(err.message || "Failed to load goal");
    }
    setLoading(false);
  }, [goalId]);

  React.useEffect(() => { fetchGoal(); }, [fetchGoal]);

  // --- Lifecycle handlers (chrome-level) ---
  const handleMarkAchieved = async () => {
    if (achievePending) return;
    setAchievePending(true);
    try {
      await apiFetch(`/api/organizing-goals/${goalId}`, {
        method: "PUT",
        body: JSON.stringify({ status: "achieved" }),
      });
      toast.show("Goal achieved! 🎉", "success");
      await fetchGoal();
    } catch (err: any) {
      toast.show(err.message || "Failed to update goal", "error");
    }
    setAchievePending(false);
  };

  const handlePauseGoal = async () => {
    try {
      await apiFetch(`/api/organizing-goals/${goalId}`, {
        method: "PUT",
        body: JSON.stringify({ status: "paused" }),
      });
      toast.show("Goal paused. You can resume anytime.", "success");
      await fetchGoal();
    } catch (err: any) {
      toast.show(err.message || "Failed to pause goal", "error");
    }
  };

  const handleResumeGoal = async () => {
    try {
      await apiFetch(`/api/organizing-goals/${goalId}`, {
        method: "PUT",
        body: JSON.stringify({ status: "active" }),
      });
      toast.show("Goal resumed!", "success");
      await fetchGoal();
    } catch (err: any) {
      toast.show(err.message || "Failed to resume goal", "error");
    }
  };

  const handleAbandonGoal = async () => {
    try {
      await apiFetch(`/api/organizing-goals/${goalId}`, {
        method: "PUT",
        body: JSON.stringify({ status: "abandoned" }),
      });
      toast.show("Goal let go. Nothing was deleted — you can always come back to it.", "success");
      setShowAbandonConfirm(false);
      navigate("/organize/goals");
    } catch (err: any) {
      toast.show(err.message || "Failed to abandon goal", "error");
    }
  };

  const handleEditGoal = async () => {
    if (!editTitle.trim()) return;
    try {
      await apiFetch(`/api/organizing-goals/${goalId}`, {
        method: "PUT",
        body: JSON.stringify({
          title: editTitle.trim(),
          description: editDescription.trim() || null,
          activityLocationId: editLocationId || null,
        }),
      });
      toast.show("Goal updated", "success");
      setShowEditGoal(false);
      await fetchGoal();
    } catch (err: any) {
      toast.show(err.message || "Failed to update goal", "error");
    }
  };

  const openEditGoal = () => {
    if (!goal) return;
    setEditTitle(goal.title);
    setEditDescription(goal.description ?? "");
    setEditLocationId(goal.activityLocationId ?? "");
    setShowEditGoal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (loadError || !goal) {
    return (
      <div className="bg-error-wash rounded-lg border border-clay-border text-center py-16 px-6">
        <AlertTriangle className="w-10 h-10 text-error mx-auto mb-3" />
        <p className="text-error-text mb-1">{loadError || "Goal not found"}</p>
        <div className="flex items-center justify-center gap-3 mt-3">
          <button onClick={() => { setLoading(true); fetchGoal(); }} className="px-4 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
            Try again
          </button>
          <button onClick={() => navigate("/organize/goals")} className="px-4 py-2.5 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors">
            Back to goals
          </button>
        </div>
      </div>
    );
  }

  const activityLocationName = goal.activityLocationId ? spaces.find((s) => s.id === goal.activityLocationId)?.name : null;

  // Focus the goal detail on the goal's TYPE. Older goals default to
  // "find_home" (matching the migration), so behavior is unchanged for them.
  const goalType: GoalType = goal.type ?? "find_home";

  return (
    <div className="space-y-6">
      {/* Back button + header */}
      <div>
        <button onClick={() => navigate("/organize/goals")} className="flex items-center gap-1 text-sm text-faint hover:text-ink mb-3 py-2">
          <ArrowLeft className="w-4 h-4" />Back to goals
        </button>
        <div className="flex items-start justify-between">
          <div>
            <span className="inline-block text-[0.8125rem] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-pill bg-amethyst-soft text-amethyst-ink mb-1.5">
              {GOAL_TYPE_LABELS[goalType]}
            </span>
            <h1 className="text-2xl font-semibold text-ink font-headline tracking-[-0.01em]">{goal.title}</h1>
            {goal.description && <p className="text-sm text-body mt-1">{goal.description}</p>}
            {activityLocationName && (
              <div className="flex items-center gap-1 text-[0.8125rem] text-faint mt-2">
                <MapPin className="w-3 h-3" />{activityLocationName}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {goal.status === "active" && isOwner && (
              <button
                onClick={handleMarkAchieved}
                disabled={achievePending}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-sage-wash text-sage-deep text-[0.8125rem] font-medium hover:bg-sage-wash/70 transition-colors border border-clay-border disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />{achievePending ? "Saving..." : "Mark achieved"}
              </button>
            )}
            {goal.status === "achieved" && (
              <span className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-sage-wash text-sage-deep text-[0.8125rem] font-medium border border-clay-border">
                <CheckCircle2 className="w-3.5 h-3.5" />Achieved
              </span>
            )}
            {goal.status === "paused" && (
              <span className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-warning-wash text-warning-text text-[0.8125rem] font-medium border border-clay-border">
                <Pause className="w-3.5 h-3.5" />Paused
              </span>
            )}
            {/* Overflow menu for owners */}
            {isOwner && (
              <div className="relative">
                <button
                  onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                  className="p-2.5 rounded-md hover:bg-amethyst-soft transition-colors"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="w-4 h-4 text-faint" />
                </button>
                {showOverflowMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowOverflowMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white rounded-md border border-clay-border shadow-lg py-1 min-w-[180px]">
                      {goal.status === "active" && (
                        <button
                          onClick={() => { setShowOverflowMenu(false); openEditGoal(); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-body hover:bg-amethyst-soft transition-colors"
                        >
                          Edit goal
                        </button>
                      )}
                      {goal.status === "active" && (
                        <button
                          onClick={() => { setShowOverflowMenu(false); handlePauseGoal(); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-body hover:bg-amethyst-soft transition-colors"
                        >
                          Pause goal
                        </button>
                      )}
                      {goal.status === "paused" && (
                        <button
                          onClick={() => { setShowOverflowMenu(false); handleResumeGoal(); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-body hover:bg-amethyst-soft transition-colors"
                        >
                          Resume goal
                        </button>
                      )}
                      {["active", "paused"].includes(goal.status) && (
                        <button
                          onClick={() => { setShowOverflowMenu(false); setShowAbandonConfirm(true); }}
                          className="w-full text-left px-4 py-2.5 text-sm text-error-text hover:bg-error-wash transition-colors"
                        >
                          Let go of this goal
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-type flow view */}
      {(() => {
        switch (goalType) {
          case "reorganize":
            return <ReorganizeGoalView goal={goal} goalId={goalId!} isOwner={isOwner} onSetGoal={setGoal} refetchGoal={fetchGoal} />;
          case "declutter":
            return <DeclutterGoalView goal={goal} goalId={goalId!} isOwner={isOwner} onSetGoal={setGoal} refetchGoal={fetchGoal} />;
          case "routine":
            return <RoutineGoalView goalId={goalId!} goalStatus={goal.status} isOwner={isOwner} />;
          case "find_home":
          default:
            return <PlacementGoalView goal={goal} goalId={goalId!} isOwner={isOwner} onSetGoal={setGoal} refetchGoal={fetchGoal} />;
        }
      })()}

      {/* Edit Goal modal */}
      <Modal open={showEditGoal} onClose={() => setShowEditGoal(false)} title="Edit goal">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Title</label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              maxLength={200}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Description (optional)</label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none"
              rows={3}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-body mb-1 block">Activity location</label>
            <select
              value={editLocationId}
              onChange={(e) => setEditLocationId(e.target.value)}
              className="w-full px-3 py-2.5 border border-clay-border rounded-sm text-sm text-body focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
            >
              <option value="">None</option>
              {spaces.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={handleEditGoal}
            disabled={!editTitle.trim()}
            className="w-full py-3 bg-primary text-white rounded-md text-sm font-medium hover:bg-amethyst-ink transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed"
          >
            Save changes
          </button>
        </div>
      </Modal>

      {/* Abandon confirmation */}
      <Modal open={showAbandonConfirm} onClose={() => setShowAbandonConfirm(false)} title="Let go of this goal?">
        <div className="space-y-4">
          <p className="text-sm text-body">
            This won't delete anything — your items stay where they are, and you can always come back to this goal later.
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleAbandonGoal}
              className="flex-1 py-3 bg-error-wash text-error-text rounded-md text-sm font-medium hover:bg-error/15 transition-colors"
            >
              Yes, let it go
            </button>
            <button
              onClick={() => setShowAbandonConfirm(false)}
              className="flex-1 py-3 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors"
            >
              Keep it
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
